import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Card,
  Space,
  Checkbox,
  Drawer,
  Typography,
  Spin,
  message,
  Tag,
  Divider,
  Input,
  Image,
} from 'antd';
import {
  DownloadOutlined,
  SettingOutlined,
  TableOutlined,
  ReloadOutlined,
  SearchOutlined,
  PictureOutlined,
  EditOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { API_URL } from '../config';
import RoomTypeEdit from './edit/RoomTypeEdit';
import RoomEdit from './edit/RoomEdit';
import ProductEdit from './edit/ProductEdit';
import FloorEdit from './edit/FloorEdit';
import BulkCreateRoomsModal from './BulkCreateRoomsModal';
import BulkPushFeaturesModal from './BulkPushFeaturesModal';
import BulkPushRoomTypesModal from './BulkPushRoomTypesModal';
import BulkPushFloorsModal from './BulkPushFloorsModal';
import SyncRoomDescriptionsModal from './SyncRoomDescriptionsModal';
import SyncRoomNamesModal from './SyncRoomNamesModal';
import CreateFloorModal from './CreateFloorModal';
import CreateRoomFeatureModal from './CreateRoomFeatureModal';
import AssignFeatureToRoomsModal from './AssignFeatureToRoomsModal';

const { Title, Text } = Typography;
const { Search } = Input;

interface TableBuilderProps {
  dataType: string;
  sessionId?: string;
  rawData?: any;
  hotelId?: number;
  emHotelId?: number;
  pmsType?: 'mews' | 'guestline' | 'opera_cloud';
}

const TableBuilder: React.FC<TableBuilderProps> = ({ dataType, sessionId, rawData, hotelId, emHotelId, pmsType }) => {
  const [data, setData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [tableFilteredData, setTableFilteredData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [availableFields, setAvailableFields] = useState<string[]>([]);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<any>(null);
  const [bulkCreateModalVisible, setBulkCreateModalVisible] = useState(false);
  const [selectedRoomType, setSelectedRoomType] = useState<any>(null);
  const [bulkPushFeaturesModalVisible, setBulkPushFeaturesModalVisible] = useState(false);
  const [bulkPushFloorsModalVisible, setBulkPushFloorsModalVisible] = useState(false);
  const [bulkPushRoomTypesModalVisible, setBulkPushRoomTypesModalVisible] = useState(false);
  const [syncRoomDescriptionsModalVisible, setSyncRoomDescriptionsModalVisible] = useState(false);
  const [syncRoomNamesModalVisible, setSyncRoomNamesModalVisible] = useState(false);
  const [createFloorModalVisible, setCreateFloorModalVisible] = useState(false);
  const [createRoomFeatureModalVisible, setCreateRoomFeatureModalVisible] = useState(false);
  const [assignFeatureModalVisible, setAssignFeatureModalVisible] = useState(false);
  const [featureToAssign, setFeatureToAssign] = useState<{ mewsId: string; name: string } | null>(null);

  useEffect(() => {
    fetchData();
  }, [dataType, sessionId, hotelId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let fetchedData;

      // Use hotel entity endpoints if hotelId is provided
      if (hotelId) {
        const endpointMap: Record<string, string> = {
          'room_types': 'room-types',
          'rooms': 'rooms',
          'room_features': 'room-features',
          'products': 'products',
          'images': 'images',
          'floors': 'floors',
        };

        const endpoint = endpointMap[dataType];
        if (!endpoint) {
          throw new Error(`Unknown data type: ${dataType}`);
        }

        const response = await axios.get(`${API_URL}/hotels/${hotelId}/${endpoint}`);
        fetchedData = response.data;
      } else {
        // Fallback to session-based preview
        const response = await axios.post(`${API_URL}/pms/preview`, {
          sessionId,
          type: dataType,
        });
        fetchedData = response.data.data;
      }

      setData(fetchedData);
      setFilteredData(fetchedData);

      // Extract available fields from the first record
      if (fetchedData.length > 0) {
        const fields = Object.keys(fetchedData[0]);
        setAvailableFields(fields);

        // Hide unnecessary columns by default
        const hiddenFields = [
          'imageUrlHighRes', // Hide high-res URL (we show thumb with download)
          'imageIds', // Hide raw image IDs array
          'serviceId', // Hide service ID (we show serviceName)
          'categoryId', // Hide category ID (we show categoryName)
          'productId', // Hide product ID (we show id)
          'promotions', // Hide JSON promotions
          'options', // Hide JSON options
          'classifications', // Hide JSON classifications
        ];

        const visibleFields = fields.filter(field => !hiddenFields.includes(field));

        // Move imageUrlThumb to 2nd position if it exists
        const imageThumbIndex = visibleFields.indexOf('imageUrlThumb');
        if (imageThumbIndex > -1) {
          // Remove imageUrlThumb from its current position
          const imageThumbField = visibleFields.splice(imageThumbIndex, 1)[0];
          // Insert it at position 1 (2nd column, after the first column)
          visibleFields.splice(1, 0, imageThumbField);
        }

        setSelectedFields(visibleFields);
      }
    } catch (err) {
      message.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Handle global search across all fields
  const handleSearch = (value: string) => {
    setSearchText(value);
    if (!value) {
      setFilteredData(data);
      return;
    }

    const searchLower = value.toLowerCase();
    const filtered = data.filter((record) =>
      Object.values(record).some((val) =>
        val?.toString().toLowerCase().includes(searchLower)
      )
    );
    setFilteredData(filtered);
  };

  const handleFieldToggle = (field: string) => {
    setSelectedFields((prev) =>
      prev.includes(field)
        ? prev.filter((f) => f !== field)
        : [...prev, field]
    );
  };

  const handleExport = async () => {
    if (selectedFields.length === 0) {
      message.warning('Please select at least one field to export');
      return;
    }

    setExporting(true);
    try {
      // Export table filtered data (includes both search and column filters)
      const dataToExport = tableFilteredData.length > 0 ? tableFilteredData : filteredData;

      // Create CSV content
      const headers = selectedFields.join(',');
      const rows = dataToExport.map((row) =>
        selectedFields.map((field) => {
          const value = row[field]?.toString() || '';
          // Escape quotes and wrap in quotes if contains comma or newline
          if (value.includes(',') || value.includes('\n') || value.includes('"')) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      );

      const csv = [headers, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${dataType}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      message.success(`CSV exported successfully! ${dataToExport.length} records exported.`);
    } catch (err) {
      message.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  // Explicit overrides for field names whose auto-titlecased form is either wrong
  // (e.g. mewsId → "Mews Id" — we show PMS-agnostic copy across Mews and Guestline)
  // or awkward (UTC timestamps). Keep keys camelCase to match the field names.
  const COLUMN_LABEL_OVERRIDES: Record<string, string> = {
    mewsId: 'PMS ID',
    mewsImageId: 'PMS Image ID',
    mewsCreatedUtc: 'Created (UTC)',
    mewsUpdatedUtc: 'Updated (UTC)',
  };

  const getColumnLabel = (key: string) => {
    if (COLUMN_LABEL_OVERRIDES[key]) return COLUMN_LABEL_OVERRIDES[key];
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  // Get unique values for a field for filter dropdown
  const getUniqueValues = (field: string) => {
    const values = filteredData.map((row) => row[field]?.toString() || '');
    return Array.from(new Set(values)).filter(Boolean).sort();
  };

  // Check if field has empty values
  const hasEmptyValues = (field: string) => {
    return filteredData.some((row) => {
      const value = row[field]?.toString() || '';
      return !value || value === '-' || value.trim() === '';
    });
  };

  // Build Ant Design table columns from selected fields with filters
  const columns = selectedFields.map((field) => {
    const uniqueValues = getUniqueValues(field);
    const hasEmpty = hasEmptyValues(field);
    const isImageField = field.toLowerCase().includes('image') || field.toLowerCase().includes('url');

    // Check if this is a thumbnail field (for both room images and product images)
    const isThumbField = field === 'imageUrlThumb';

    // Check if this is the isActive field
    const isActiveField = field === 'isActive';

    const isDescField = field.toLowerCase().includes('description');

    // Check if this field contains object values (JSON fields like names, descriptions)
    const hasObjectValues = filteredData.some((row) => {
      const val = row[field];
      return val !== null && typeof val === 'object' && !Array.isArray(val);
    });

    return {
      title: getColumnLabel(field),
      dataIndex: field,
      key: field,
      ellipsis: !isImageField && !isDescField,
      ...(isDescField ? {
        width: 300,
        render: (text: any) => {
          const display = (text && typeof text === 'object') ? JSON.stringify(text) : (text || '-');
          return (
            <div style={{
              maxWidth: 280,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }} title={typeof display === 'string' ? display : ''}>
              {display}
            </div>
          );
        },
      } : {}),
      // Custom render for isActive field with colored tags
      ...(isActiveField ? {
        render: (value: boolean) => (
          <Tag color={value ? 'green' : 'red'}>
            {value ? 'Active' : 'Inactive'}
          </Tag>
        ),
        width: 100,
      } : {}),
      // Default render for object values (JSON fields like names, descriptions)
      ...(!isDescField && !isActiveField && !isThumbField && hasObjectValues ? {
        render: (value: any) => {
          if (value === null || value === undefined) return '-';
          if (typeof value === 'object') {
            const entries = Object.entries(value);
            if (entries.length === 0) return '-';
            return entries.map(([k, v]) => `${k}: ${v}`).join(', ');
          }
          return value;
        },
      } : {}),
      // Custom render for thumbnail fields
      ...(isThumbField ? {
        render: (url: string, record: any) => {
          if (!url) return '-';

          // Get the high-res URL from the same record
          const highResUrl = record.imageUrlHighRes || url.replace('Height=100&Width=150', 'Height=1080&Width=2048');

          const handleDownload = (e: React.MouseEvent) => {
            e.stopPropagation();
            const link = document.createElement('a');
            link.href = highResUrl;
            link.download = `product_${record.productId || record.id}_image.jpg`;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            message.success('Downloading image...');
          };

          return (
            <Space>
              <Image
                src={url}
                alt="Product"
                width={60}
                height={40}
                style={{ objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                preview={{
                  src: highResUrl
                }}
              />
              <Button
                type="link"
                size="small"
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                title="Download high-res image"
              />
            </Space>
          );
        },
        width: 130,
      } : {}),
      // Add sorting
      sorter: (a: any, b: any) => {
        const aVal = a[field]?.toString() || '';
        const bVal = b[field]?.toString() || '';
        return aVal.localeCompare(bVal);
      },
      // Add filtering - always show filters with non-empty option
      filters: [
        // Add "Non-empty" filter at the top if there are empty values
        ...(hasEmpty ? [{
          text: '✓ Non-empty only',
          value: '__non_empty__',
        }] : []),
        // Add "Empty" filter if there are empty values
        ...(hasEmpty ? [{
          text: '○ Empty only',
          value: '__empty__',
        }] : []),
        // Add divider if we added special filters
        ...(hasEmpty && uniqueValues.length > 0 ? [{
          text: '─────────',
          value: '__divider__',
        }] : []),
        // Add all unique values (limit to 50)
        ...uniqueValues.slice(0, 50).map((value) => ({
          text: value,
          value: value,
        })),
        // Show message if there are more values
        ...(uniqueValues.length > 50 ? [{
          text: `... and ${uniqueValues.length - 50} more`,
          value: '__more__',
          disabled: true,
        }] : []),
      ],
      onFilter: (value: any, record: any) => {
        const fieldValue = record[field]?.toString() || '';

        // Handle special filters
        if (value === '__non_empty__') {
          return fieldValue && fieldValue !== '-' && fieldValue.trim() !== '';
        }
        if (value === '__empty__') {
          return !fieldValue || fieldValue === '-' || fieldValue.trim() === '';
        }
        if (value === '__divider__' || value === '__more__') {
          return true; // Dividers don't filter
        }

        // Handle normal value filtering
        return fieldValue === value.toString();
      },
      filterSearch: true,
    };
  });

  // Add Actions column for editable data types
  if (hotelId && ['room_types', 'rooms', 'products', 'floors'].includes(dataType)) {
    (columns as any[]).push({
      title: 'Actions',
      key: 'actions',
      fixed: 'right' as const,
      width: 80,
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingRecord(record);
              setEditModalVisible(true);
            }}
          >
            Edit
          </Button>
        </Space>
      ),
    });
  }

  // Room features: single-action column to assign the feature to rooms in bulk.
  if (hotelId && dataType === 'room_features') {
    (columns as any[]).push({
      title: 'Actions',
      key: 'actions',
      fixed: 'right' as const,
      width: 160,
      render: (_: any, record: any) => {
        const featureMewsId = record.mewsId;
        const featureName = record.name || record.shortName || record.mewsId;
        if (!featureMewsId) return null;
        return (
          <Button
            type="link"
            onClick={() => {
              setFeatureToAssign({ mewsId: featureMewsId, name: featureName });
              setAssignFeatureModalVisible(true);
            }}
          >
            Assign to Rooms
          </Button>
        );
      },
    });
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" tip="Loading data..." />
      </div>
    );
  }

  return (
    <div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: 12,
        }}>
          <div style={{ flex: '1 1 220px', minWidth: 220 }}>
            <Title level={4} style={{ marginBottom: 4 }}>
              {getColumnLabel(dataType)}
            </Title>
            <Text type="secondary">
              {tableFilteredData.length > 0 && tableFilteredData.length !== filteredData.length
                ? `${tableFilteredData.length} filtered`
                : `${filteredData.length}`}
              {filteredData.length !== data.length && ` of ${data.length}`} records • {selectedFields.length}/{availableFields.length} fields selected
            </Text>
          </div>

          <Space wrap>
            {dataType === 'room_types' && hotelId && emHotelId && (
              <Button
                type="primary"
                onClick={() => setBulkPushRoomTypesModalVisible(true)}
              >
                Bulk Push All Room Types
              </Button>
            )}
            {dataType === 'rooms' && hotelId && (
              <Button
                onClick={() => setSyncRoomDescriptionsModalVisible(true)}
              >
                Sync Descriptions from Room Type
              </Button>
            )}
            {dataType === 'rooms' && hotelId && (
              <Button
                onClick={() => setSyncRoomNamesModalVisible(true)}
              >
                Sync Names from Room Type
              </Button>
            )}
            {dataType === 'rooms' && hotelId && emHotelId && (
              <Button
                type="primary"
                onClick={() => setBulkCreateModalVisible(true)}
              >
                Bulk Push All Rooms
              </Button>
            )}
            {dataType === 'room_features' && hotelId && pmsType !== 'mews' && (
              <Button
                onClick={() => setCreateRoomFeatureModalVisible(true)}
              >
                Create Room Feature
              </Button>
            )}
            {dataType === 'room_features' && hotelId && emHotelId && (
              <Button
                type="primary"
                onClick={() => setBulkPushFeaturesModalVisible(true)}
              >
                Bulk Push All Features
              </Button>
            )}
            {dataType === 'floors' && hotelId && pmsType !== 'mews' && (
              <Button
                onClick={() => setCreateFloorModalVisible(true)}
              >
                Create Floor
              </Button>
            )}
            {dataType === 'floors' && hotelId && emHotelId && (
              <Button
                type="primary"
                onClick={() => setBulkPushFloorsModalVisible(true)}
              >
                Bulk Push All Floors
              </Button>
            )}
            <Search
              placeholder="Search across all fields..."
              allowClear
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              onSearch={handleSearch}
              style={{ width: 300 }}
              prefix={<SearchOutlined />}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchData}
            >
              Refresh
            </Button>
            <Button
              icon={<SettingOutlined />}
              onClick={() => setDrawerOpen(true)}
            >
              Customize Table
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExport}
              loading={exporting}
              disabled={selectedFields.length === 0}
            >
              Export CSV
            </Button>
          </Space>
        </div>

        <Table
          dataSource={filteredData}
          columns={columns}
          rowKey={(record, index) => index?.toString() || '0'}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} items`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          scroll={{ x: 'max-content' }}
          bordered
          onChange={(pagination, filters, sorter, extra) => {
            // Capture filtered data from table (includes column filters)
            setTableFilteredData(extra.currentDataSource || []);
          }}
        />

      <Drawer
        title={
          <Space>
            <TableOutlined />
            <span>Customize Table Fields</span>
          </Space>
        }
        placement="right"
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        width={400}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            Select the fields you want to display and export
          </Text>
        </div>

        <Space style={{ marginBottom: 16 }}>
          <Button
            size="small"
            onClick={() => setSelectedFields(availableFields)}
          >
            Select All
          </Button>
          <Button
            size="small"
            onClick={() => setSelectedFields([])}
          >
            Deselect All
          </Button>
        </Space>

        <Divider />

        <Space direction="vertical" style={{ width: '100%' }}>
          {availableFields.map((field) => (
            <Card
              key={field}
              size="small"
              style={{
                background: selectedFields.includes(field) ? '#f0f5ff' : '#fafafa',
                cursor: 'pointer',
              }}
              onClick={() => handleFieldToggle(field)}
            >
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <div>
                  <Checkbox
                    checked={selectedFields.includes(field)}
                    onChange={() => handleFieldToggle(field)}
                  >
                    <Text strong>{getColumnLabel(field)}</Text>
                  </Checkbox>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12, marginLeft: 24 }}>
                    {field}
                  </Text>
                </div>
                {selectedFields.includes(field) && (
                  <Tag color="blue">Selected</Tag>
                )}
              </Space>
            </Card>
          ))}
        </Space>

        <Divider />

        <Button
          type="primary"
          block
          onClick={() => {
            setDrawerOpen(false);
            message.success('Table customization applied!');
          }}
        >
          Apply Changes
        </Button>
      </Drawer>

      {/* Room Type Edit Modal */}
      {dataType === 'room_types' && hotelId && editingRecord && (
        <RoomTypeEdit
          roomTypeId={editingRecord.dbId || parseInt(editingRecord.id?.toString().split('_')[0] || '0')}
          hotelId={hotelId}
          visible={editModalVisible}
          onClose={() => {
            setEditModalVisible(false);
            setEditingRecord(null);
          }}
          onSuccess={() => {
            fetchData();
          }}
          emHotelId={emHotelId}
        />
      )}

      {/* Room Edit Modal */}
      {dataType === 'rooms' && hotelId && editingRecord && (
        <RoomEdit
          roomId={editingRecord.id}
          hotelId={hotelId}
          visible={editModalVisible}
          onClose={() => {
            setEditModalVisible(false);
            setEditingRecord(null);
          }}
          onSuccess={() => {
            fetchData();
          }}
          emHotelId={emHotelId}
        />
      )}

      {/* Product Edit Modal */}
      {dataType === 'products' && hotelId && editingRecord && (
        <ProductEdit
          productId={editingRecord.dbId || editingRecord.id}
          hotelId={hotelId}
          visible={editModalVisible}
          onClose={() => {
            setEditModalVisible(false);
            setEditingRecord(null);
          }}
          onSuccess={() => {
            fetchData();
          }}
        />
      )}

      {/* Floor Edit Modal */}
      {dataType === 'floors' && hotelId && editingRecord && (
        <FloorEdit
          floorId={editingRecord.id}
          hotelId={hotelId}
          visible={editModalVisible}
          onClose={() => {
            setEditModalVisible(false);
            setEditingRecord(null);
          }}
          onSuccess={() => {
            fetchData();
          }}
          emHotelId={emHotelId}
        />
      )}

      {/* Bulk Push All Room Types Modal - For Room Types Table */}
      {dataType === 'room_types' && hotelId && emHotelId && (
        <BulkPushRoomTypesModal
          visible={bulkPushRoomTypesModalVisible}
          hotelId={hotelId}
          emHotelId={emHotelId}
          onClose={() => {
            setBulkPushRoomTypesModalVisible(false);
          }}
          onSuccess={() => {
            fetchData();
          }}
        />
      )}

      {/* Bulk Push All Rooms Modal - For Rooms Table */}
      {dataType === 'rooms' && hotelId && emHotelId && (
        <BulkCreateRoomsModal
          visible={bulkCreateModalVisible}
          hotelId={hotelId}
          roomTypeId={0}
          roomTypeName="All Rooms"
          emHotelId={emHotelId}
          onClose={() => {
            setBulkCreateModalVisible(false);
          }}
          onSuccess={() => {
            fetchData();
          }}
        />
      )}

      {/* Bulk Push All Features Modal - For Room Features Table */}
      {dataType === 'room_features' && hotelId && emHotelId && (
        <BulkPushFeaturesModal
          visible={bulkPushFeaturesModalVisible}
          hotelId={hotelId}
          emHotelId={emHotelId}
          onClose={() => {
            setBulkPushFeaturesModalVisible(false);
          }}
          onSuccess={() => {
            fetchData();
          }}
        />
      )}

      {/* Bulk Push All Floors Modal - For Floors Table */}
      {dataType === 'floors' && hotelId && emHotelId && (
        <BulkPushFloorsModal
          visible={bulkPushFloorsModalVisible}
          hotelId={hotelId}
          emHotelId={emHotelId}
          onClose={() => {
            setBulkPushFloorsModalVisible(false);
          }}
          onSuccess={() => {
            fetchData();
          }}
        />
      )}

      {/* Sync Room Names Modal - For Rooms Table */}
      {dataType === 'rooms' && hotelId && (
        <SyncRoomNamesModal
          visible={syncRoomNamesModalVisible}
          hotelId={hotelId}
          onClose={() => {
            setSyncRoomNamesModalVisible(false);
          }}
          onSuccess={() => {
            fetchData();
          }}
        />
      )}

      {/* Sync Room Descriptions Modal - For Rooms Table */}
      {dataType === 'rooms' && hotelId && (
        <SyncRoomDescriptionsModal
          visible={syncRoomDescriptionsModalVisible}
          hotelId={hotelId}
          onClose={() => {
            setSyncRoomDescriptionsModalVisible(false);
          }}
          onSuccess={() => {
            fetchData();
          }}
        />
      )}

      {/* Create Floor Modal - Guestline only */}
      {dataType === 'floors' && hotelId && pmsType !== 'mews' && (
        <CreateFloorModal
          visible={createFloorModalVisible}
          hotelId={hotelId}
          onClose={() => {
            setCreateFloorModalVisible(false);
          }}
          onSuccess={() => {
            fetchData();
          }}
        />
      )}

      {/* Create Room Feature Modal - Guestline only */}
      {dataType === 'room_features' && hotelId && pmsType !== 'mews' && (
        <CreateRoomFeatureModal
          visible={createRoomFeatureModalVisible}
          hotelId={hotelId}
          onClose={() => {
            setCreateRoomFeatureModalVisible(false);
          }}
          onSuccess={() => {
            fetchData();
          }}
        />
      )}

      {/* Assign Feature to Rooms Modal */}
      {dataType === 'room_features' && hotelId && featureToAssign && (
        <AssignFeatureToRoomsModal
          visible={assignFeatureModalVisible}
          hotelId={hotelId}
          featureMewsId={featureToAssign.mewsId}
          featureName={featureToAssign.name}
          onClose={() => {
            setAssignFeatureModalVisible(false);
            setFeatureToAssign(null);
          }}
          onSuccess={() => {
            fetchData();
          }}
        />
      )}
    </div>
  );
};

export default TableBuilder;
